import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  DialogActions,
  Button,
  IconButton,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { RHFSelect, RHFTextField } from '../hook-form';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from '../hook-form/form-provider';
import { getRequest, postRequest } from 'src/services';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { handleErrorMessages } from '../risk-management/risk-register/feching';
import Iconify from '../iconify';
import { TitleCategory } from './dropdown-descirption';
import { endpoints } from 'src/utils/axios';

const TradeWorkTitle = ({ isOpen, handleClose, reload, AttachMoneyIcon }) => {
  const [loading, setLoading] = useState(false);
  const NewUserSchema = Yup.object().shape({
    title_code: Yup.string().required('Code is required'),
    name: Yup.string().required('Trade/Worker Title Description is required'),
    title_category: Yup.string(),
    base_wage: Yup.number(),
    suppl_benefits: Yup.number(),
    workers_comp_insurance: Yup.number(),
    misc_expanses: Yup.number(),
    employer_taxes: Yup.number(),
    regular_time_rate: Yup.string(),
    double_time_rate: Yup.string(),
    over_time_rate: Yup.string(),
  });

  const defaultValues = useMemo(
    () => ({
      title_code: '',
      name: '',
      title_category: '',
      base_wage: 0,
      suppl_benefits: 0,
      workers_comp_insurance: 0,
      misc_expanses: 0,
      employer_taxes: 0,
      regular_time_rate: '',
      double_time_rate: '',
      over_time_rate: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });
  const { reset, setValue, getValues, handleSubmit } = methods;
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onClose = () => {
    handleClose();
    reset();
  };

  const onSubmit = handleSubmit(async (formData) => {
    setLoading(true);
    try {
      const postResponse = await postRequest(endpoints.ganttChart.post_trade_worker_title, {
        ...formData,
        project_id: localStorage.getItem('projectId'),
      });

      const responseData = await getRequest(
        `${endpoints.ganttChart.get_trade_worker_titles}?project_id=${localStorage.getItem(
          'projectId'
        )}`
      );

      if (postResponse?.metadata && postResponse?.metadata?.success) {
        onClose();
        reload(responseData);
        enqueueSnackbar('Trade Wroker Title created successfully', { variant: 'success' });
      } else {
        handleErrorMessages(postResponse?.metadata?.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      enqueueSnackbar('An error occurred while submitting the form.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  });

  const calculationOfRegularTimeRate = (data, keyName) => {
    setValue(keyName, data);

    const rateKeys = [
      'base_wage',
      'suppl_benefits',
      'workers_comp_insurance',
      'misc_expanses',
      'employer_taxes',
    ];

    const regularTimeRate = rateKeys.reduce((total, rateKey) => {
      const value = rateKey === keyName ? data : getValues(rateKey);
      return total + (parseFloat(value) || 0);
    }, 0);

    setValue('regular_time_rate', regularTimeRate);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'content-box',
          }}
        >
          <span
            className="cardColor"
            style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
          >
            &nbsp;&nbsp; Create Trade / Worker Title &nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <IconButton onClick={handleClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box
            mt={2}
            mb={2}
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField name="title_code" label="Code" />
            <RHFTextField name="name" label="Trade/Worker Title Description" />

            <>
              <RHFSelect name="title_category" label="Title category">
                {TitleCategory?.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </RHFSelect>
              <RHFTextField
                type="number"
                name="base_wage"
                label="Base Wage"
                onChange={(e) => {
                  calculationOfRegularTimeRate(e.target.value, 'base_wage');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {AttachMoneyIcon}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                type="number"
                name="suppl_benefits"
                label="Suppl.Benefits"
                onChange={(e) => {
                  calculationOfRegularTimeRate(e.target.value, 'suppl_benefits');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {AttachMoneyIcon}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                type="number"
                name="workers_comp_insurance"
                label="Workers Comp Insurance"
                onChange={(e) => {
                  calculationOfRegularTimeRate(e.target.value, 'workers_comp_insurance');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {AttachMoneyIcon}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                type="number"
                name="misc_expanses"
                label="Misc. Expenses"
                onChange={(e) => {
                  calculationOfRegularTimeRate(e.target.value, 'misc_expanses');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {AttachMoneyIcon}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                type="number"
                name="employer_taxes"
                label="Employee Taxes"
                onChange={(e) => {
                  calculationOfRegularTimeRate(e.target.value, 'employer_taxes');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {AttachMoneyIcon}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </>

            <RHFTextField
              disabled
              type="number"
              name="regular_time_rate"
              label="Regular Time Rate"
            />
            <RHFTextField type="number" name="double_time_rate" label="Double Time Rate" />
            <RHFTextField type="number" name="over_time_rate" label="Over Time Rate" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            className="dark-button"
            disabled={loading}
            loading={loading}
            type="submit"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
export default TradeWorkTitle;
